import axios from 'axios'
import { TFunction } from 'i18next'
import * as forge from 'node-forge'

import { GGClientError } from '@/types/error'

export const extractErrorMessage = (error: unknown) => {
	const ggError = GGClientError.is(error) ? error : (error as Record<string, unknown>)
	let innerError = ggError?.cause

	if (innerError && typeof innerError === 'object' && 'errors' in innerError && Array.isArray(innerError.errors)) {
		innerError = innerError.errors.find(axios.isAxiosError)
	}

	const message = axios.isAxiosError<{ error?: string }>(innerError) ? innerError.response?.data?.error : undefined
	return {
		message,
	}
}

export const generateAutoAppName = (str: string) => {
	return str
		.trim()
		.replace(/[^a-zA-Z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
}

export const SYNC_TYPES = {
	always: 'always_sync',
	syncOnFirstLogin: 'sync_on_first_login_only',
	syncUntilUserChanges: 'sync_until_user_changes',
} as const

export type SyncType = (typeof SYNC_TYPES)[keyof typeof SYNC_TYPES]

export const USER_FIELDS_MAP = {
	mandatory: {
		invoice_profile_name: {
			idp_attribute: 'invoice_profile',
			sync_type: SYNC_TYPES.always,
		},
		email: {
			idp_attribute: 'emailaddress',
			sync_type: SYNC_TYPES.always,
		},
		last_name: {
			idp_attribute: 'surname',
			sync_type: SYNC_TYPES.always,
		},
		first_name: {
			idp_attribute: 'givenname',
			sync_type: SYNC_TYPES.always,
		},
		phone: {
			idp_attribute: 'phone_number',
			sync_type: SYNC_TYPES.always,
		},
	},
	optional: {
		middle_name: {
			label: (t: TFunction) => t('Middle Name'),
			idp_attribute: 'middle_name',
			sync_type: SYNC_TYPES.always,
		},
		gender: {
			label: (t: TFunction) => t('Gender'),
			idp_attribute: 'gender',
			sync_type: SYNC_TYPES.always,
		},
		date_of_birth: {
			label: (t: TFunction) => t('Date of Birth'),
			idp_attribute: 'date_of_birth',
			sync_type: SYNC_TYPES.always,
		},
		title: {
			label: (t: TFunction) => t('Title'),
			idp_attribute: 'title',
			sync_type: SYNC_TYPES.always,
		},
		seniority_level: {
			label: (t: TFunction) => t('Seniority Level'),
			idp_attribute: 'seniority_level',
			sync_type: SYNC_TYPES.always,
		},
		deactivation_date: {
			label: (t: TFunction) => t('Deactivation Date'),
			idp_attribute: 'deactivation_date',
			sync_type: SYNC_TYPES.always,
		},
		emergency_contact_name: {
			label: (t: TFunction) => t('Emergency Contact Name'),
			idp_attribute: 'emergency_contact_name',
			sync_type: SYNC_TYPES.always,
		},
		emergency_contact_phone: {
			label: (t: TFunction) => t('Emergency Contact Phone Number'),
			idp_attributes: 'emergency_contact_phone',
			sync_type: SYNC_TYPES.always,
		},

		emergency_contact_email: {
			label: (t: TFunction) => t('Emergency Contact Email'),
			idp_attributes: 'emergency_contact_email',
			sync_type: SYNC_TYPES.always,
		},
		known_traveler_number: {
			label: (t: TFunction) => t('United States Known Traveler Number'),
			idp_attributes: 'known_traveler_number',
			sync_type: SYNC_TYPES.always,
		},
		redress_number: {
			label: (t: TFunction) => t('United States Redress Number'),
			idp_attributes: 'redress_number',
			sync_type: SYNC_TYPES.always,
		},
		canadian_travel_number: {
			label: (t: TFunction) => t('Canadian Travel Number'),
			idp_attributes: 'canadian_travel_number',
			sync_type: SYNC_TYPES.always,
		},
		airline_loyalty: {
			label: (t: TFunction) => t('Airline Loyalty'),
			idp_attribute: 'airline_loyalty_code',
			sync_type: SYNC_TYPES.always,
		},
		airline_number: {
			label: (t: TFunction) => t('Airline Number'),
			idp_attribute: 'airline_loyalty_number',
			sync_type: SYNC_TYPES.always,
		},
		airline_loyalty_2: {
			label: (t: TFunction) => t('Airline Loyalty 2'),
			idp_attribute: 'airline_loyalty_code_2',
			sync_type: SYNC_TYPES.always,
		},
		airline_number_2: {
			label: (t: TFunction) => t('Airline Number 2'),
			idp_attribute: 'airline_loyalty_number_2',
			sync_type: SYNC_TYPES.always,
		},
		hotel_loyalty: {
			label: (t: TFunction) => t('Hotel Loyalty'),
			idp_attribute: 'hotel_loyalty_code',
			sync_type: SYNC_TYPES.always,
		},
		hotel_number: {
			label: (t: TFunction) => t('Hotel Number'),
			idp_attribute: 'hotel_loyalty_number',
			sync_type: SYNC_TYPES.always,
		},
		hotel_loyalty_2: {
			label: (t: TFunction) => t('Hotel Loyalty 2'),
			idp_attribute: 'hotel_loyalty_code_2',
			sync_type: SYNC_TYPES.always,
		},
		hotel_number_2: {
			label: (t: TFunction) => t('Hotel Number 2'),
			idp_attribute: 'hotel_loyalty_number_2',
			sync_type: SYNC_TYPES.always,
		},
		car_loyalty: {
			label: (t: TFunction) => t('Car Loyalty'),
			idp_attribute: 'car_loyalty_code',
			sync_type: SYNC_TYPES.always,
		},
		car_number: {
			label: (t: TFunction) => t('Car Number'),
			idp_attribute: 'car_loyalty_number',
			sync_type: SYNC_TYPES.always,
		},
		car_loyalty_2: {
			label: (t: TFunction) => t('Car Loyalty 2'),
			idp_attribute: 'car_loyalty_code_2',
			sync_type: SYNC_TYPES.always,
		},
		car_number_2: {
			label: (t: TFunction) => t('Car number 2'),
			idp_attribute: 'car_loyalty_number_2',
			sync_type: SYNC_TYPES.always,
		},
	},
}

type SyncTypeLabels = {
	getTitle: (t: TFunction) => string
	getDescription: (t: TFunction) => string
}

export const syncTypelabels: SyncTypeLabels[] = [
	{
		getTitle: (t: TFunction) => t('Always Sync'),
		getDescription: (t: TFunction) => t('When a user logs in, we will refresh this field with the latest value.'),
	},
	{
		getTitle: (t: TFunction) => t('Sync on first login only'),
		getDescription: (t: TFunction) =>
			t('We will populate this field when the user first logs in and will not change it afterward.'),
	},
	{
		getTitle: (t: TFunction) => t('Sync on first login only'),
		getDescription: (t: TFunction) =>
			t('We will update this field each time a user logs in, unless they have updated it themselves in GetGoing.'),
	},
]
type JsonValue = string | { [key: string]: JsonValue } | JsonValue[]

export const xmlToJson = (node: Element): JsonValue => {
	if (node.children.length === 0) {
		return node.textContent?.trim() ?? ''
	}

	const obj: Record<string, JsonValue> = {}

	if (node.attributes.length > 0) {
		const attributes: Record<string, JsonValue> = {}

		Array.from(node.attributes).forEach((attr) => {
			attributes[attr.name] = attr.value
		})

		obj['@attributes'] = attributes
	}

	Array.from(node.children).forEach((child) => {
		const childData = xmlToJson(child)

		if (obj[child.nodeName]) {
			if (!Array.isArray(obj[child.nodeName])) {
				obj[child.nodeName] = [obj[child.nodeName]]
			}

			;(obj[child.nodeName] as JsonValue[]).push(childData)
		} else {
			obj[child.nodeName] = childData
		}
	})

	return obj
}

export type CertificateMetadata = {
	certificate: string
	signInUrl: string
	isValid: boolean
	expiryDate: Date
	isExpiredWithinSixMonth: boolean
}

export const certificateToBase64 = async (file: File): Promise<CertificateMetadata | null> => {
	try {
		const text = (await file.text()).trim()

		const xmlDoc = new DOMParser().parseFromString(text, 'application/xml')

		if (xmlDoc.querySelector('parsererror')) {
			return null
		}

		const certNode = xmlDoc.getElementsByTagNameNS('*', 'X509Certificate')[0]

		if (!certNode?.textContent?.trim()) {
			return null
		}

		const certificate = certNode.textContent.replace(/\s+/g, '')

		const signInUrl = Array.from(xmlDoc.getElementsByTagNameNS('*', 'SingleSignOnService'))
			.find((node) => node.getAttribute('Binding') === 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect')
			?.getAttribute('Location')

		if (!signInUrl) {
			return null
		}

		const der = forge.util.decode64(certificate)
		const asn1 = forge.asn1.fromDer(der)
		const cert = forge.pki.certificateFromAsn1(asn1)

		const expiryDate = cert.validity.notAfter

		const sixMonthsFromNow = new Date()
		sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6)

		return {
			certificate,
			signInUrl,
			expiryDate,
			isValid: expiryDate > sixMonthsFromNow,
			isExpiredWithinSixMonth: expiryDate <= sixMonthsFromNow,
		}
	} catch (error) {
		console.error('Failed to parse SAML metadata:', error)
		return null
	}
}
